import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, AlertTriangle, TrendingUp, Info } from "lucide-react";
import { useProjectVarianceDrivers } from "@/hooks/useProjectVarianceDrivers";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import type { ProjectAlert, AlertSeverity } from "@/hooks/useProjectAlerts";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

const pct = (n: number) => `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`;

const varianceColor = (v: number) =>
  v > 0 ? "text-green-600" : v < 0 ? "text-destructive" : "text-muted-foreground";

const ALERT_BADGE_COLORS: Record<AlertSeverity, string> = {
  critical: "bg-destructive/15 text-destructive border-destructive/30",
  warning: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30",
  info: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30",
};

interface Props {
  projectId: string;
  alerts?: ProjectAlert[];
}

interface SovLineVariance {
  sov_description: string;
  unit: string;
  budgeted_rate: number;
  wo_rate: number;
  assigned_qty: number;
  total_sov_qty: number;
  unassigned_qty: number;
  variance: number;
  contractor_name: string | null;
  source_type: string | null;
}

export const VarianceExplanationCard = ({ projectId, alerts = [] }: Props) => {
  const { data, isLoading } = useProjectVarianceDrivers(projectId);

  // Per-SOV-line variance breakdown
  const lineVarianceQ = useQuery({
    queryKey: ["variance_line_detail", projectId],
    queryFn: async () => {
      // Get all SOV lines for this project with their WO line details
      const { data: rows, error } = await supabase
        .from("work_order_lines")
        .select(`
          quantity,
          contract_rate,
          sov_line_id,
          sov_lines!inner (
            description,
            unit,
            quantity,
            sub_unit_price,
            source_type
          ),
          work_orders!inner (
            project_id,
            status,
            contractors:contractor_id (
              name
            )
          )
        `)
        .eq("work_orders.project_id", projectId)
        .not("work_orders.status", "in", '("cancelled","rejected")')
        .neq("status", "voided" as any);

      if (error) throw error;

      // Group by sov_line_id and aggregate
      const byLine: Record<string, SovLineVariance> = {};
      (rows ?? []).forEach((r: any) => {
        const sl = r.sov_lines;
        const id = r.sov_line_id;
        if (!byLine[id]) {
          byLine[id] = {
            sov_description: sl.description ?? "",
            unit: sl.unit ?? "EA",
            budgeted_rate: Number(sl.sub_unit_price ?? 0),
            wo_rate: 0,
            assigned_qty: 0,
            total_sov_qty: Number(sl.quantity ?? 0),
            unassigned_qty: Number(sl.quantity ?? 0),
            variance: 0,
            contractor_name: r.work_orders?.contractors?.name ?? null,
            source_type: sl.source_type,
          };
        }
        const qty = Number(r.quantity ?? 0);
        const rate = Number(r.contract_rate ?? 0);
        byLine[id].assigned_qty += qty;
        // weighted average rate
        byLine[id].wo_rate =
          (byLine[id].wo_rate * (byLine[id].assigned_qty - qty) + rate * qty) /
          byLine[id].assigned_qty;
      });

      // Calculate variance and unassigned
      return Object.values(byLine).map((l) => ({
        ...l,
        wo_rate: Math.round(l.wo_rate * 100) / 100,
        unassigned_qty: l.total_sov_qty - l.assigned_qty,
        variance: (l.budgeted_rate - l.wo_rate) * l.assigned_qty,
      })).sort((a, b) => a.variance - b.variance); // worst first
    },
    enabled: !!projectId,
  });

  // Unassigned SOV lines (no WO at all)
  const unassignedQ = useQuery({
    queryKey: ["variance_unassigned", projectId],
    queryFn: async () => {
      const { data: contracts } = await supabase
        .from("contracts")
        .select("id")
        .eq("project_id", projectId);
      const contractIds = (contracts ?? []).map((c) => c.id);
      if (contractIds.length === 0) return [];

      const { data: allSov } = await supabase
        .from("sov_lines")
        .select("id, description, unit, quantity, sub_unit_price, source_type")
        .in("contract_id", contractIds);

      const { data: assignedIds } = await supabase
        .from("work_order_lines")
        .select("sov_line_id")
        .not("status", "eq", "voided");

      const assignedSet = new Set((assignedIds ?? []).map((r: any) => r.sov_line_id));
      return (allSov ?? [])
        .filter((s: any) => !assignedSet.has(s.id) && Number(s.quantity) > 0)
        .map((s: any) => ({
          description: s.description,
          unit: s.unit,
          quantity: Number(s.quantity),
          budgeted_rate: Number(s.sub_unit_price ?? 0),
          budgeted_cost: Number(s.quantity) * Number(s.sub_unit_price ?? 0),
          source_type: s.source_type,
        }));
    },
    enabled: !!projectId,
  });

  const lineVariance = lineVarianceQ.data ?? [];
  const unassignedLines = unassignedQ.data ?? [];
  const totalLineVariance = lineVariance.reduce((s, l) => s + l.variance, 0);
  const totalUnassignedBudget = unassignedLines.reduce((s, l) => s + l.budgeted_cost, 0);

  const [committedDriversOpen, setCommittedDriversOpen] = useState(true);
  const [actualDriversOpen, setActualDriversOpen] = useState(true);
  const [overridesOpen, setOverridesOpen] = useState(false);
  const [unattributedOpen, setUnattributedOpen] = useState(false);
  const [lineBreakdownOpen, setLineBreakdownOpen] = useState(true);
  const [unassignedOpen, setUnassignedOpen] = useState(false);

  if (isLoading || !data) return null;

  const { summary: s, drivers: d, top_overrides, top_unattributed, data_quality: dq } = data;

  const hasVariance = s.committed_margin_variance !== 0 || s.actual_margin_variance !== 0 ||
    d.override_exposure !== 0 || d.po_overrun !== 0 || d.unattributed_actual !== 0;

  // Committed cost drivers (overrides + PO overruns)
  const committedDriversList = [
    { label: "Subcontractor Rate Overrides", amount: d.override_exposure, count: d.override_line_count },
    { label: "Material PO Overruns", amount: d.po_overrun, count: d.po_overrun_line_count },
  ]
    .filter((x) => x.amount !== 0)
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));

  // Actual cost drivers (unattributed expenses)
  const actualDriversList = [
    { label: "Unattributed Actual Cost", amount: d.unattributed_actual, count: d.unattributed_expense_count },
  ].filter((x) => x.amount !== 0);

  const committedCostVariance = s.committed_cost - s.estimated_cost;

  // Determine header icon: alert for erosion, trending-up for improvement
  const overallVariance = s.committed_margin_variance;
  const HeaderIcon = overallVariance < 0 ? AlertTriangle : overallVariance > 0 ? TrendingUp : Info;

  const hasDataQualityNotes = dq.wo_lines_missing_sov_rate > 0 || dq.unattributed_expense_count > 0 || dq.unattributed_po_count > 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <HeaderIcon className="h-4 w-4 text-muted-foreground" />
          Variance Explanation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Margin Erosion Summary */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Margin Summary
          </h4>
          <Table className="w-full table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[25%]"></TableHead>
                <TableHead className="text-right w-[25%] text-xs">Estimated</TableHead>
                <TableHead className="text-right w-[25%] text-xs">Committed</TableHead>
                <TableHead className="text-right w-[25%] text-xs">Actual</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium text-xs">Cost</TableCell>
                <TableCell className="text-right text-xs">{fmt(s.estimated_cost)}</TableCell>
                <TableCell className="text-right text-xs">{fmt(s.committed_cost)}</TableCell>
                <TableCell className="text-right text-xs">{fmt(s.actual_cost)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium text-xs">Margin</TableCell>
                <TableCell className="text-right text-xs">{s.estimated_margin_pct.toFixed(1)}%</TableCell>
                <TableCell className="text-right text-xs">{s.committed_margin_pct.toFixed(1)}%</TableCell>
                <TableCell className="text-right text-xs">{s.actual_margin_pct.toFixed(1)}%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium text-xs">Variance</TableCell>
                <TableCell className="text-right text-muted-foreground text-xs">—</TableCell>
                <TableCell className={`text-right font-semibold text-xs ${varianceColor(s.committed_margin_variance)}`}>
                  {pct(s.committed_margin_variance)}
                </TableCell>
                <TableCell className={`text-right font-semibold text-xs ${varianceColor(s.actual_margin_variance)}`}>
                  {pct(s.actual_margin_variance)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {!hasVariance && (
          <div className="flex items-center gap-2 rounded-md border border-dashed p-3">
            <Info className="h-4 w-4 text-muted-foreground shrink-0" />
            <p className="text-sm text-muted-foreground">No variance detected — all costs align with estimates.</p>
          </div>
        )}

        {/* Committed Variance Drivers */}
        {committedDriversList.length > 0 && (
          <Collapsible open={committedDriversOpen} onOpenChange={setCommittedDriversOpen}>
            <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors w-full">
              <ChevronDown className={`h-3 w-3 transition-transform ${committedDriversOpen ? "" : "-rotate-90"}`} />
              Committed Cost Drivers
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 space-y-1.5">
                {committedDriversList.map((drv) => {
                  const matchCategory = drv.label.includes("Override") ? "override" : "po_overrun";
                  const matchedAlert = alerts.find((a) => a.category === matchCategory);
                  return (
                    <div key={drv.label} className="flex items-center justify-between rounded-md border px-3 py-2 gap-2">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <span className="text-xs font-medium truncate">{drv.label}</span>
                        <Badge variant="outline" className="text-[10px] shrink-0">{drv.count} lines</Badge>
                        {matchedAlert && (
                          <Badge variant="outline" className={`text-[10px] shrink-0 ${ALERT_BADGE_COLORS[matchedAlert.severity]}`}>
                            {matchedAlert.severity}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-xs font-semibold ${drv.amount > 0 ? "text-destructive" : "text-green-600"}`}>
                          {drv.amount > 0 ? "+" : ""}{fmt(drv.amount)}
                        </span>
                        {committedCostVariance !== 0 && (
                          <span className="text-[10px] text-muted-foreground hidden sm:inline">
                            {Math.abs(Math.round((drv.amount / committedCostVariance) * 100))}%
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Actual Cost Drivers */}
        {actualDriversList.length > 0 && (
          <Collapsible open={actualDriversOpen} onOpenChange={setActualDriversOpen}>
            <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors w-full">
              <ChevronDown className={`h-3 w-3 transition-transform ${actualDriversOpen ? "" : "-rotate-90"}`} />
              Actual Cost Drivers
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 space-y-1.5">
                {actualDriversList.map((drv) => {
                  const matchedAlert = alerts.find((a) => a.category === "unattributed");
                  return (
                    <div key={drv.label} className="flex items-center justify-between rounded-md border px-3 py-2 gap-2">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <span className="text-xs font-medium truncate">{drv.label}</span>
                        <Badge variant="outline" className="text-[10px] shrink-0">{drv.count} items</Badge>
                        {matchedAlert && (
                          <Badge variant="outline" className={`text-[10px] shrink-0 ${ALERT_BADGE_COLORS[matchedAlert.severity]}`}>
                            {matchedAlert.severity}
                          </Badge>
                        )}
                      </div>
                      <span className={`text-xs font-semibold shrink-0 ${drv.amount > 0 ? "text-destructive" : "text-green-600"}`}>
                        {fmt(drv.amount)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Override Cost Impact */}
        {top_overrides.length > 0 && (
          <Collapsible open={overridesOpen} onOpenChange={setOverridesOpen}>
            <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors w-full">
              <ChevronDown className={`h-3 w-3 transition-transform ${overridesOpen ? "" : "-rotate-90"}`} />
              Override Cost Impact
            </CollapsibleTrigger>
            <CollapsibleContent>
              <Table className="w-full table-fixed mt-2">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs w-[28%]">SOV Line</TableHead>
                    <TableHead className="text-right text-xs w-[14%]">Est. Rate</TableHead>
                    <TableHead className="text-right text-xs w-[14%]">Override</TableHead>
                    <TableHead className="text-right text-xs w-[10%]">Qty</TableHead>
                    <TableHead className="text-right text-xs w-[14%]">Impact</TableHead>
                    <TableHead className="text-xs w-[20%]">Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {top_overrides.map((o, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs truncate">{o.sov_line_description}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{fmt(o.estimated_rate)}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{fmt(o.override_rate)}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{o.quantity}</TableCell>
                      <TableCell className={`text-right text-xs font-semibold tabular-nums ${o.impact > 0 ? "text-destructive" : "text-green-600"}`}>
                        {o.impact > 0 ? "+" : ""}{fmt(o.impact)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate">
                        {o.reason || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-1 text-right text-xs font-semibold text-muted-foreground">
                Total Override Exposure: <span className="text-foreground">{fmt(d.override_exposure)}</span>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Unattributed Actual Cost */}
        {top_unattributed.length > 0 && (
          <Collapsible open={unattributedOpen} onOpenChange={setUnattributedOpen}>
            <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors w-full">
              <ChevronDown className={`h-3 w-3 transition-transform ${unattributedOpen ? "" : "-rotate-90"}`} />
              Unattributed Actual Cost
            </CollapsibleTrigger>
            <CollapsibleContent>
              <Table className="w-full table-fixed mt-2">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs w-[50%]">Description</TableHead>
                    <TableHead className="text-right text-xs w-[25%]">Amount</TableHead>
                    <TableHead className="text-xs w-[25%]">Category</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {top_unattributed.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs truncate">{e.description}</TableCell>
                      <TableCell className="text-right text-xs font-semibold tabular-nums">{fmt(e.amount)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{e.category || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-1 text-right text-xs font-semibold text-muted-foreground">
                Total Unattributed: <span className="text-foreground">{fmt(d.unattributed_actual)}</span>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Per-SOV-Line Variance Breakdown */}
        {lineVariance.length > 0 && (
          <Collapsible open={lineBreakdownOpen} onOpenChange={setLineBreakdownOpen}>
            <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors w-full">
              <ChevronDown className={`h-3 w-3 transition-transform ${lineBreakdownOpen ? "" : "-rotate-90"}`} />
              Per-Line Variance Breakdown
              <Badge variant="outline" className="ml-1 text-[10px]">{lineVariance.length} lines</Badge>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <Table className="w-full table-fixed mt-2">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs w-[22%]">SOV Line</TableHead>
                    <TableHead className="text-xs w-[13%]">Contractor</TableHead>
                    <TableHead className="text-right text-xs w-[13%]">Budget</TableHead>
                    <TableHead className="text-right text-xs w-[13%]">WO Rate</TableHead>
                    <TableHead className="text-right text-xs w-[11%]">Diff</TableHead>
                    <TableHead className="text-right text-xs w-[14%]">Assigned</TableHead>
                    <TableHead className="text-right text-xs w-[14%]">Variance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineVariance.map((l, i) => {
                    const diff = l.budgeted_rate - l.wo_rate;
                    return (
                      <TableRow key={i}>
                        <TableCell className="text-xs truncate">
                          <div className="flex items-center gap-1">
                            {l.source_type === "change_order" && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 border-green-500 text-green-700 bg-green-50 shrink-0">CO</Badge>
                            )}
                            <span className="truncate">{l.sov_description}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground truncate">
                          {l.contractor_name || "—"}
                        </TableCell>
                        <TableCell className="text-right text-xs tabular-nums">
                          {fmt(l.budgeted_rate)}/{l.unit}
                        </TableCell>
                        <TableCell className="text-right text-xs tabular-nums">
                          {fmt(l.wo_rate)}/{l.unit}
                        </TableCell>
                        <TableCell className={`text-right text-xs tabular-nums font-medium ${diff > 0 ? "text-green-600" : diff < 0 ? "text-destructive" : ""}`}>
                          {diff > 0 ? "+" : ""}{fmt(diff)}
                        </TableCell>
                        <TableCell className="text-right text-xs tabular-nums">
                          {l.assigned_qty.toLocaleString()} {l.unit}
                        </TableCell>
                        <TableCell className={`text-right text-xs tabular-nums font-semibold ${l.variance > 0 ? "text-green-600" : l.variance < 0 ? "text-destructive" : ""}`}>
                          {l.variance > 0 ? "+" : ""}{fmt(l.variance)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="mt-1 flex justify-between text-xs font-semibold text-muted-foreground px-1">
                <span>Net Sub Variance:</span>
                <span className={totalLineVariance >= 0 ? "text-green-600" : "text-destructive"}>
                  {totalLineVariance > 0 ? "+" : ""}{fmt(totalLineVariance)}
                </span>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Unassigned SOV Lines */}
        {unassignedLines.length > 0 && (
          <Collapsible open={unassignedOpen} onOpenChange={setUnassignedOpen}>
            <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors w-full">
              <ChevronDown className={`h-3 w-3 transition-transform ${unassignedOpen ? "" : "-rotate-90"}`} />
              Unassigned SOV Lines
              <Badge variant="outline" className="ml-1 text-[10px]">{unassignedLines.length} lines</Badge>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <Table className="w-full table-fixed mt-2">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs w-[40%]">SOV Line</TableHead>
                    <TableHead className="text-right text-xs w-[20%]">Qty</TableHead>
                    <TableHead className="text-right text-xs w-[20%]">Budget Rate</TableHead>
                    <TableHead className="text-right text-xs w-[20%]">Budget Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unassignedLines.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs truncate">
                        <div className="flex items-center gap-1">
                          {l.source_type === "change_order" && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 border-green-500 text-green-700 bg-green-50 shrink-0">CO</Badge>
                          )}
                          <span className="truncate">{l.description}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums">
                        {l.quantity.toLocaleString()} {l.unit}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums">
                        {l.budgeted_rate > 0 ? `${fmt(l.budgeted_rate)}/${l.unit}` : "—"}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums">
                        {l.budgeted_cost > 0 ? fmt(l.budgeted_cost) : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-1 text-right text-xs font-semibold text-muted-foreground">
                Total Unassigned Budget: <span className="text-foreground">{fmt(totalUnassignedBudget)}</span>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Data Quality Notes */}
        {hasDataQualityNotes && (
          <div className="rounded-md border border-dashed p-3 space-y-1">
            <div className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              <Info className="h-3 w-3" />
              Data Quality
            </div>
            {dq.wo_lines_missing_sov_rate > 0 && (
              <p className="text-xs text-muted-foreground">
                {dq.wo_lines_missing_sov_rate} work order line{dq.wo_lines_missing_sov_rate !== 1 ? "s" : ""} created before rate tracking lack baseline sub rates — override exposure may be understated.
              </p>
            )}
            {dq.unattributed_expense_count > 0 && (
              <p className="text-xs text-muted-foreground">
                {dq.unattributed_expense_count} approved expense{dq.unattributed_expense_count !== 1 ? "s" : ""} not attributed to an SOV line.
              </p>
            )}
            {dq.unattributed_po_count > 0 && (
              <p className="text-xs text-muted-foreground">
                {dq.unattributed_po_count} purchase order{dq.unattributed_po_count !== 1 ? "s" : ""} not linked to an SOV line — per-line overrun calculation may be incomplete.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
